import { todayISO } from '../shared/functions/dateUtils.js'

const _today = new Date()

// Static config arrays — outside state to avoid signal wrapping.
export const DOC_FOLDERS = Object.freeze([
  { key: 'all', label: 'All documents', icon: 'folder' },
  { key: 'company', label: 'Company', icon: 'flag' },
  { key: 'legal', label: 'Legal', icon: 'bookmark' },
  { key: 'finance', label: 'Finance', icon: 'document' },
  { key: 'people', label: 'People & HR', icon: 'people' },
  { key: 'engineering', label: 'Engineering', icon: 'code' },
  { key: 'design', label: 'Design', icon: 'eye' }
])

export const KB_CATEGORIES = Object.freeze([
  { key: 'all', label: 'All articles', icon: 'document' },
  { key: 'getting-started', label: 'Getting Started', icon: 'star' },
  { key: 'engineering', label: 'Engineering', icon: 'code' },
  { key: 'design', label: 'Design System', icon: 'eye' },
  { key: 'policies', label: 'Policies', icon: 'bookmark' },
  { key: 'tools', label: 'Tools & Infra', icon: 'settings' }
])

export default {
  isRootState: true,

  currentUserEmail: '',
  currentUserId: '',
  signedIn: false,
  companyName: 'Symbols',

  // Auth claims + org context — populated by subscribeAuthSession from the
  // governance JWT. Without these declared, smbls 3.14 silently drops the
  // updates and org switching / People filter / role gates all break.
  jwtClaims: null,
  orgs: [],
  workspaces: [],
  org: null,
  workspace: null,
  _wsSwitching: null,
  // Optimistic-pick override for AppWorkspaceSwitcher. When the user
  // clicks an org in the dropdown, this gets set to the picked org id
  // so `_activeOrg(root)` (and thus the Trigger label + checkmark)
  // reflects the choice this same tick instead of waiting for the
  // server's JWT refresh. Cleared once the workspace-change handler
  // fires (or on switch error).
  _pendingActiveOrgId: null,
  _wsRefreshing: false,
  _loginLoading: false,
  orgNotifCount: {},
  orgNotifSignals: {},
  // Financials project data (PROJECT_FINANCIALS optional federation).
  companySettings: null,
  userGrants: [],
  valuations: [],

  selectedGroup: null,
  selectedRole: null,
  currentPath: typeof window !== 'undefined' ? window.location.pathname : '/',
  navSearchQuery: '',
  navSearchResults: [],
  _navSearchGrouped: [],
  _navSearchLoading: false,
  _navSearchOpen: false,
  navUnreadCounts: {},
  contentSection: null,

  people: [],
  peopleAll: [],
  teams: [],
  teamsByEmail: {},
  orgMembersByOrgId: {},
  userDocs: [],
  userPrefs: null,
  resourceLinks: [],
  _wsMenuOpen: false,
  announcements: [],
  selectedAnnouncement: null,
  stories: [],
  birthdays: [],
  companyInfo: [],
  roles: { available: [], byUser: {}, version: 0 },

  // ── Calendar ──
  viewMode: 'week',
  currentYear: _today.getFullYear(),
  currentMonth: _today.getMonth(),
  currentDay: _today.getDate(),
  currentMinutes: _today.getHours() * 60 + _today.getMinutes(),
  selectedEvent: null,
  showNewBooking: false,
  showEventDetail: false,
  selectedPerson: null,
  profileEmail: null,
  profileModalTab: 'profile', // 'profile' | 'availability' | 'settings' — used by the unified ProfileModal
  dragPreview: null,
  quickCreate: null,
  hoverTime: null,
  teamMembers: [],
  onlineUsers: [],
  newBooking: {
    title: '',
    date: todayISO(),
    startTime: '09:00',
    endTime: '10:00',
    invitees: '',
    inviteeQuery: '',
    description: '',
    type: 'meeting',
    meetingMode: 'symbols-meet',
    recurring: 'none',
    recurringDays: [],
    editingEventId: null
  },
  events: [],

  // ── Tickets ──
  columns: [],
  tickets: [],
  loadedByColumn: {},
  totalByColumn: {},
  loadingColumn: {},
  epicOptions: [],
  epicCounts: {},
  myTickets: [],
  recentTickets: [],
  myViewQueueOpen: false,
  _activityData: {},
  _todayUpdate: null,
  _dailyDraft: '',
  _dailyUpdates: [],
  _standupMessages: [],
  _standupUserName: '',
  _standupHistory: {},
  _standupPhase: 'idle',
  _standupSummary: '',
  _standupActions: [],
  _standupFollowUps: [],
  _ticketComments: [],
  _mentionModal: { open: false },
  filterAssignee: null,
  filterLabel: null,
  searchQuery: '',

  // ── Requests ──
  // Backed by workspace-project SDK fetches (`requests.{customer,inbound,outbound}`,
  // `audit.reports`). Pages drive content from these arrays, so any seed
  // value (fixture rows, demo content) ships to every signed-in user. Keep
  // these empty so the empty-state UX and the real backend wiring own
  // what users see.
  customerRequests: [],
  inboundRequests: [],
  outboundRequests: [],
  auditReports: [],

  // ── Resolutions ──
  // Same reasoning as the request arrays above — empty until the
  // workspace-project backend serves the real rows for suggestions /
  // ai-improvement-prompts / pull-requests under the active workspace.
  suggestions: [],
  improvementRequests: [],
  editRequests: [],
  selectedTicket: null,
  ticketDraft: null,
  ticketEditMode: false,
  activeDrag: null,
  dropPreview: null,
  syncing: false,

  // ── Chat ──
  channels: [],
  channelMembers: [],
  activeChannelId: null,
  showNewChannel: false,
  newChannelDraft: { name: '', topic: '', kind: 'channel', is_private: false, members: [] },
  messagesByChannel: {},
  activeThreadRootId: null,
  presence: {},
  typingByChannel: {},
  reactionsByMessage: {},
  myMentions: [],
  collapsedGroups: {},
  searchOpen: false,
  searchResults: [],
  mentionPicker: null,
  sidebarDropdown: { open: false },
  _hiddenChannels: [],
  _pinnedChannels: [],
  renameModal: { open: false },
  pendingAttachments: {},
  composerDraft: {},
  composerPreview: false,
  chatVisibleCounts: null,
  activeTab: 'conversation',
  showFilterBar: false,
  filters: {
    authorEmail: null,
    hasAttachments: false,
    hasMentions: false,
    query: ''
  },

  // ── Documents ──
  activeSection: 'documents',
  selectedFolder: 'all',
  selectedDocument: null,
  docsQuery: '',
  documents: [],
  selectedNoteId: null,
  notesFilter: 'all',
  notes: [],
  editorDoc: null,
  editorMode: null,
  kbQuery: '',
  selectedCategory: 'all',
  kbArticles: [],

  // ── Lightbox ──
  lightbox: null,

  // ── AI Assistant ──
  assistantOpen: false,
  assistantMessages: [],
  assistantTyping: false,
  assistantAttachment: null,

  // ── Notifications ──
  notifications: [],
  notificationsPanelOpen: false
}
